import { useState } from "react";
import { ActivityIndicator } from "react-native";
import { Redirect, useRouter } from "expo-router";

import { AuthScreen } from "~/components/composed/AuthScreen";
import {
  AppText,
  Button,
  ButtonText,
  CenteredScreen,
  SegmentedControl,
  Stack,
  TextField,
} from "~/components/ui";
import { useAuth } from "~/hooks/useAuth";
import { useCollectionActions, useCollections } from "~/state/collections";
import { fetchCollections } from "~/state/sync";
import {
  COLLECTIONS_PATH,
  isInitialOnboardingComplete,
  withOnboardingComplete,
} from "~/utils/onboarding";

export default function OnboardingScreen() {
  const router = useRouter();
  const {
    session,
    user,
    loading,
    updateProfile,
    updatePreferences,
    updatePreferencesPending,
    updateProfilePending,
    refresh,
  } = useAuth();
  const collections = useCollections();
  const collectionActions = useCollectionActions();

  const [name, setName] = useState("My garden");
  const [units, setUnits] = useState<"metric" | "imperial">(
    user?.units === "imperial" ? "imperial" : "metric",
  );
  const [error, setError] = useState<string | null>(null);

  if (loading) {
    return (
      <CenteredScreen>
        <ActivityIndicator />
      </CenteredScreen>
    );
  }

  if (!session?.user) {
    return <Redirect href="/login" />;
  }

  if (isInitialOnboardingComplete(user)) {
    return <Redirect href={COLLECTIONS_PATH as never} />;
  }

  const finish = async (rename: boolean) => {
    setError(null);
    try {
      const trimmed = name.trim();
      // Signup creates a starter collection, so rename it rather than stacking
      // a second one against the free-tier limit.
      // (The list may not have loaded yet right after sign-up, so ask the API.)
      const starter = collections[0] ?? (await fetchCollections())[0];
      if (rename && trimmed && starter) {
        collectionActions.update(starter.id, { name: trimmed });
      }
      await updateProfile({ units });
      await updatePreferences(withOnboardingComplete(user?.preferences));
      await refresh();
      router.replace(COLLECTIONS_PATH as never);
    } catch (finishError) {
      setError(
        finishError instanceof Error
          ? finishError.message
          : "Could not finish setting up.",
      );
    }
  };

  const busy = updatePreferencesPending || updateProfilePending;

  return (
    <AuthScreen
      title="Welcome to Florafolio"
      subtitle="Two quick questions, then you can start cataloguing. Everything works offline and syncs when you're back on."
    >
      <Stack gap="md">
        {error ? (
          <AppText tone="danger" size="xs">
            {error}
          </AppText>
        ) : null}

        <Stack gap="xs">
          <AppText size="xs" weight="medium">
            What should we call your first collection?
          </AppText>
          <TextField
            accessibilityLabel="First collection name"
            value={name}
            onChangeText={setName}
            placeholder="My garden, Houseplants, The greenhouse..."
          />
          <AppText size="xs">
            Collections group plants however suits you — by room, by garden bed,
            or by the trip you brought them home from.
          </AppText>
        </Stack>

        <Stack gap="xs">
          <AppText size="xs" weight="medium">
            Measurements
          </AppText>
          <SegmentedControl
            value={units}
            options={[
              { label: "Metric (cm)", value: "metric" },
              { label: "Imperial (in)", value: "imperial" },
            ]}
            onChange={(value) => setUnits(value as "metric" | "imperial")}
          />
        </Stack>

        <Button disabled={busy} onPress={() => void finish(true)}>
          <ButtonText>{busy ? "Saving..." : "Start cataloguing"}</ButtonText>
        </Button>
        <Button
          variant="outline"
          disabled={busy}
          onPress={() => void finish(false)}
        >
          <ButtonText>Skip for now</ButtonText>
        </Button>
      </Stack>
    </AuthScreen>
  );
}
